import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { usePortalAuth } from '../context/PortalAuthContext';
import { AdminAPI } from '../services/portalApi';

export const PortalInviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setFullToken } = usePortalAuth();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [name, setName] = useState('');
  const [adminId, setAdminId] = useState('');
  const [qrBase64, setQrBase64] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await AdminAPI.acceptInvite({ invite_token: token, name });
      setAdminId(data.admin_id);
      setQrBase64(data.totp_qr_base64);
      setTotpUri(data.totp_uri);
      setStep(2);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await AdminAPI.acceptInviteVerifyTotp({ admin_id: adminId, code: totpCode });
      sessionStorage.setItem('ip_portal_token', data.token);
      setStep(4);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid TOTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterPasskey = async () => {
    setError('');
    setLoading(true);
    try {
      const { data: options } = await AdminAPI.acceptInviteRegisterDeviceStart({ admin_id: adminId });
      const credential = await startRegistration({ optionsJSON: options });
      const { data } = await AdminAPI.acceptInviteRegisterDeviceFinish({
        admin_id: adminId,
        credential,
        device_name: `${navigator.platform || 'Device'} - Setup`,
      });
      setFullToken(data.token);
      navigate('/ip-portal/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Passkey registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="portal-center-page">
      <div className="portal-center-box">
        <div className="portal-card">
          <h2>Accept Admin Invite</h2>

          <div className="portal-steps">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`portal-step ${step > s ? 'done' : step === s ? 'active' : ''}`}>
                <span className="portal-step-num">{s}</span>
                {s === 1 && 'Name'}
                {s === 2 && 'QR Code'}
                {s === 3 && 'TOTP'}
                {s === 4 && 'Passkey'}
              </div>
            ))}
          </div>

          {error && <div className="portal-alert portal-alert-error">{error}</div>}

          {step === 1 && (
            <>
              <div className="portal-form-group">
                <label>Your Name</label>
                <input
                  className="portal-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  autoFocus
                />
              </div>
              <button
                className="portal-btn portal-btn-primary"
                onClick={handleAccept}
                disabled={!name || loading}
              >
                {loading ? 'Accepting...' : 'Accept Invite'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                Scan this QR code with your authenticator app.
              </p>
              <div className="portal-qr-wrap">
                <img src={`data:image/png;base64,${qrBase64}`} alt="TOTP QR Code" />
              </div>
              <div className="portal-form-group">
                <label>Manual Entry URI</label>
                <input className="portal-input" readOnly value={totpUri} onClick={(e) => (e.target as HTMLInputElement).select()} />
              </div>
              <button className="portal-btn portal-btn-primary" onClick={() => setStep(3)}>
                I've Scanned the QR Code
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div className="portal-form-group">
                <label>Enter TOTP Code</label>
                <input
                  className="portal-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  onKeyDown={(e) => e.key === 'Enter' && totpCode.length === 6 && handleVerifyTotp()}
                  autoFocus
                />
              </div>
              <button
                className="portal-btn portal-btn-primary"
                onClick={handleVerifyTotp}
                disabled={totpCode.length !== 6 || loading}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </>
          )}

          {step === 4 && (
            <>
              <div className="portal-alert portal-alert-info">
                TOTP verified! Register a passkey to complete setup.
              </div>
              <button
                className="portal-btn portal-btn-primary"
                onClick={handleRegisterPasskey}
                disabled={loading}
              >
                {loading ? 'Registering...' : 'Register Passkey'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
